import { type ChangeEvent, useState } from "react";
import { centsToInputValue, parsePriceDollars } from "../lib/money";
import styles from "./MoneyInput.module.css";

export interface MoneyInputProps {
	/** Canonical value in integer cents. */
	valueCents: number;
	/** Receives the parsed cents on every keystroke. */
	onChange: (cents: number) => void;
	name?: string;
	placeholder?: string;
	ariaLabel?: string;
	/** Associates the control with an external error/hint element. */
	ariaDescribedBy?: string;
	/** Marks the control as failing validation for assistive technology. */
	invalid?: boolean;
	disabled?: boolean;
	/**
	 * "framed" (default) is the form control. "inline" drops the frame until
	 * hover/focus, for money fields sitting in a list of values — see the Scope
	 * line-item rows.
	 */
	variant?: "framed" | "inline";
}

/**
 * Dollars-and-cents text input bound to an integer-cents value.
 *
 * The editable text is local state (initialised from the cents value on mount)
 * so typing stays natural — intermediate states like "12." are not rounded
 * under the user's cursor. The draft only ever sees whole cents, which keeps
 * float precision out of the data. Remounting (e.g. after page navigation)
 * re-derives the text from the canonical cents, so nothing is lost.
 *
 * The field carries a "$" adornment and right-aligned tabular figures, so a
 * money control is recognisable as one and a column of them lines up by place
 * value. The symbol is decoration — the parsed value never includes it.
 */
export function MoneyInput({
	valueCents,
	onChange,
	name,
	placeholder = "0.00",
	ariaLabel,
	ariaDescribedBy,
	invalid,
	disabled,
	variant = "framed",
}: MoneyInputProps) {
	const [text, setText] = useState(() => centsToInputValue(valueCents));
	const inline = variant === "inline";

	const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
		const next = event.target.value;
		setText(next);
		onChange(parsePriceDollars(next));
	};

	return (
		<span className={styles.root}>
			<span
				className={inline ? styles.symbolInline : styles.symbol}
				aria-hidden="true"
			>
				$
			</span>
			<input
				className={inline ? styles.inputInline : styles.input}
				type="text"
				inputMode="decimal"
				name={name}
				value={text}
				placeholder={placeholder}
				aria-label={ariaLabel}
				aria-describedby={ariaDescribedBy}
				aria-invalid={invalid || undefined}
				disabled={disabled}
				onChange={handleChange}
			/>
		</span>
	);
}
